import React, { useContext, useEffect, useId, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import NotificationContext from "../context/NotificationContext";
import { useMutationLock } from "../hooks/useMutationLock";
import * as bugReportsApi from "../api/bugReports";
import "./BugReportModal.css";

const DEVICES = [
  { value: "mobile", label: "Mobile" },
  { value: "tablet", label: "Tablet / iPad" },
  { value: "laptop", label: "Laptop" },
  { value: "desktop", label: "Desktop" },
  { value: "other", label: "Other" },
];

const BROWSERS = [
  { value: "chrome", label: "Chrome" },
  { value: "safari", label: "Safari" },
  { value: "firefox", label: "Firefox" },
  { value: "edge", label: "Edge" },
  { value: "opera", label: "Opera" },
  { value: "other", label: "Other" },
];

const MESSAGE_MAX = 200;

export default function BugReportModal({ onClose }) {
  const { user } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { busy, run } = useMutationLock();
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  const [device, setDevice] = useState("");
  const [browser, setBrowser] = useState("");
  const [message, setMessage] = useState("");

  const email = user?.email || "";
  const trimmed = message.trim();
  const canSubmit =
    Boolean(email) &&
    Boolean(device) &&
    Boolean(browser) &&
    trimmed.length > 0 &&
    trimmed.length <= MESSAGE_MAX &&
    !busy;

  const remaining = MESSAGE_MAX - message.length;
  const counterClass =
    remaining <= 0 ? "is-full" : remaining <= 30 ? "is-near" : "";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (busy) return;
        e.preventDefault();
        onClose?.();
        return;
      }

      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, busy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    await run(async () => {
      try {
        await bugReportsApi.create({
          device,
          browser,
          message: trimmed,
        });
        onClose();
        notificationCtx.show({
          message: "Your issue has been reported successfully.",
          stat: "success",
        });
      } catch (error) {
        const status = error?.response?.status;
        const apiMessage = error?.response?.data?.message;
        let messageText = "Failed to submit bug report.";
        if (status === 429 || status === 409) {
          messageText =
            apiMessage ||
            (status === 429
              ? "You've already sent 2 reports in the last 24 hours. Please try again later."
              : "You already sent this same report recently.");
        } else if (apiMessage) {
          messageText = apiMessage;
        }
        notificationCtx.show({
          message: messageText,
          stat: "error",
        });
      }
    });
  };

  return (
    <div
      className="bug-report-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (busy) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="bug-report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="bug-report-dialog__header">
          <div className="bug-report-dialog__header-text">
            <p className="bug-report-dialog__eyebrow">Help us improve</p>
            <h2 id={titleId} className="bug-report-dialog__title">
              Report a bug
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="bug-report-dialog__close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="bug-report-dialog__body">
            <p className="bug-report-dialog__intro">
              Tell us what went wrong and what you were trying to do.
            </p>

            <div className="bug-report-dialog__email">
              <span className="bug-report-dialog__email-icon" aria-hidden="true">
                <i className="fa-solid fa-envelope" />
              </span>
              <div className="bug-report-dialog__email-meta">
                <p className="bug-report-dialog__email-label">Sending as</p>
                <p className="bug-report-dialog__email-value">{email || "—"}</p>
              </div>
            </div>

            <div className="bug-report-field">
              <span className="bug-report-field__label" id="bug-device-label">
                Device
              </span>
              <div
                className="bug-report-chips"
                role="group"
                aria-labelledby="bug-device-label"
              >
                {DEVICES.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    className={`bug-report-chip${
                      device === d.value ? " is-selected" : ""
                    }`}
                    aria-pressed={device === d.value}
                    disabled={busy}
                    onClick={() => setDevice(d.value)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bug-report-field">
              <span className="bug-report-field__label" id="bug-browser-label">
                Browser
              </span>
              <div
                className="bug-report-chips"
                role="group"
                aria-labelledby="bug-browser-label"
              >
                {BROWSERS.map((b) => (
                  <button
                    key={b.value}
                    type="button"
                    className={`bug-report-chip${
                      browser === b.value ? " is-selected" : ""
                    }`}
                    aria-pressed={browser === b.value}
                    disabled={busy}
                    onClick={() => setBrowser(b.value)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bug-report-field">
              <label className="bug-report-field__label" htmlFor="bug-message">
                What happened?
              </label>
              <textarea
                id="bug-message"
                className="bug-report-textarea"
                rows={7}
                maxLength={MESSAGE_MAX}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe the issue…"
                required
                disabled={busy}
              />
              <div className="bug-report-counter">
                <p className={`bug-report-counter__count ${counterClass}`}>
                  {message.length}/{MESSAGE_MAX}
                </p>
              </div>
            </div>
          </div>

          <div className="bug-report-dialog__footer">
            <button
              type="button"
              className="bug-report-dialog__cancel"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bug-report-dialog__submit"
              disabled={!canSubmit}
            >
              {busy ? "Sending…" : "Submit report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
